/**
 * Utilidades para descarga de archivos
 *
 * Funciones reutilizables para manejar descargas de archivos desde el frontend,
 * eliminando duplicación de código en múltiples componentes.
 */

import * as XLSX from 'xlsx';

/**
 * Extrae el nombre del archivo desde el header Content-Disposition
 *
 * @param {string} contentDisposition - Header Content-Disposition del response
 * @returns {string|null} - Nombre del archivo extraído o null si no se encuentra
 *
 * @example
 * const header = 'attachment; filename="documento.pdf"';
 * const filename = extractFilenameFromHeader(header); // "documento.pdf"
 */
export function extractFilenameFromHeader(contentDisposition) {
  if (!contentDisposition) {
    return null;
  }

  // Intentar extraer filename con comillas: filename="archivo.pdf"
  let filenameMatch = contentDisposition.match(/filename="([^"]+)"/);

  // Si no funciona, intentar sin comillas: filename=archivo.pdf
  if (!filenameMatch) {
    filenameMatch = contentDisposition.match(/filename=([^;]+)/);
  }

  if (filenameMatch && filenameMatch[1]) {
    return filenameMatch[1].trim();
  }

  return null;
}

/**
 * Descarga un archivo desde un blob con nombre específico
 *
 * Crea un link temporal, hace click programático para iniciar la descarga,
 * y limpia el objeto URL temporal para liberar memoria.
 *
 * @param {Blob} blob - Blob del archivo a descargar
 * @param {string} filename - Nombre del archivo de descarga
 *
 * @example
 * const blob = await response.blob();
 * downloadFile(blob, 'documento.pdf');
 */
export function downloadFile(blob, filename) {
  // Crear URL temporal del blob
  const downloadUrl = window.URL.createObjectURL(blob);

  // Crear link temporal
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  link.style.visibility = 'hidden';

  // Agregar al DOM, hacer click y remover
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Limpiar URL temporal para liberar memoria
  window.URL.revokeObjectURL(downloadUrl);
}

/**
 * Descarga un archivo desde una URL autenticada con manejo completo de errores
 *
 * Realiza fetch autenticado con JWT token, maneja respuestas de error,
 * extrae el nombre del archivo desde headers, y descarga el archivo.
 *
 * @param {Object} params - Parámetros de descarga
 * @param {string} params.url - URL del endpoint de descarga
 * @param {string} params.token - Token JWT de autenticación
 * @param {string} params.defaultFilename - Nombre por defecto si no se encuentra en headers
 * @param {Function} params.onSuccess - Callback ejecutado en descarga exitosa
 * @param {Function} params.onError - Callback ejecutado en caso de error (recibe mensaje de error)
 * @param {Function} params.onAuthError - Callback ejecutado si el token es inválido/expirado
 *
 * @returns {Promise<boolean>} - true si la descarga fue exitosa, false en caso contrario
 *
 * @example
 * await downloadFromAuthenticatedURL({
 *   url: `${apiService.baseURL}/api/ausentismos/${id}/documento`,
 *   token: apiService.getToken(),
 *   defaultFilename: 'documento.pdf',
 *   onSuccess: () => toast.success('Descargado'),
 *   onError: (msg) => toast.error(msg),
 *   onAuthError: () => {
 *     apiService.removeToken();
 *     toast.error('Sesión expirada');
 *   }
 * });
 */
export async function downloadFromAuthenticatedURL({
  url,
  token,
  defaultFilename = 'archivo',
  onSuccess,
  onError,
  onAuthError
}) {
  try {
    if (!token) {
      if (onError) onError('No se encontró token de autenticación');
      return false;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    // Manejo de errores HTTP
    if (!response.ok) {
      // Error de autenticación (401)
      if (response.status === 401) {
        if (onAuthError) onAuthError();
        return false;
      }

      // Archivo no encontrado (404)
      if (response.status === 404) {
        if (onError) onError('Documento no encontrado');
        return false;
      }

      // Otros errores
      if (onError) onError('Error al descargar el documento');
      return false;
    }

    // Obtener blob del archivo
    const blob = await response.blob();

    // Extraer nombre del archivo desde headers
    const contentDisposition = response.headers.get('Content-Disposition');
    const filename = extractFilenameFromHeader(contentDisposition) || defaultFilename;

    // Descargar archivo
    downloadFile(blob, filename);

    // Callback de éxito
    if (onSuccess) onSuccess();

    return true;

  } catch (error) {
    console.error('Error al descargar archivo:', error);
    if (onError) onError('Error al descargar el documento');
    return false;
  }
}

/**
 * Descarga un blob desde un endpoint autenticado extrayendo el nombre desde Content-Disposition
 *
 * Centraliza el patrón: authenticatedFormDataRequest + blob() + headers.get() + extractFilenameFromHeader.
 * Usa authenticatedFormDataRequest (no authenticatedRequest) porque retorna la Response cruda
 * con headers accesibles; authenticatedRequest descarta los headers al parsear JSON.
 *
 * @param {Function} authenticatedFormDataRequest - Función de request de FormData del módulo
 * @param {string}   endpoint        - Ruta del endpoint de descarga
 * @param {string}   defaultFilename - Nombre de archivo si Content-Disposition no está presente
 * @returns {Promise<{blob: Blob, filename: string}>}
 */
export async function downloadBlobFromEndpoint(authenticatedFormDataRequest, endpoint, defaultFilename) {
  const response = await authenticatedFormDataRequest(endpoint, { responseType: 'blob' });
  const blob     = await response.blob();
  const filename = extractFilenameFromHeader(response.headers.get('Content-Disposition'))
    || defaultFilename;
  return { blob, filename };
}

/**
 * Descarga datos en formato CSV
 *
 * Convierte un string CSV en blob y descarga el archivo.
 *
 * @param {string} csvContent - Contenido del CSV como string
 * @param {string} filename - Nombre del archivo (debe incluir extensión .csv)
 *
 * @example
 * const csv = 'nombre,email\nJuan,juan@example.com';
 * downloadCSV(csv, 'usuarios.csv');
 */
export function downloadCSV(csvContent, filename) {
  // Crear blob con tipo MIME apropiado para CSV
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  // Descargar usando helper genérico
  downloadFile(blob, filename);
}

/**
 * Descarga un array de objetos como archivo Excel (.xlsx)
 *
 * @param {Array<Object>} rows - Filas de datos (claves = nombres de columna)
 * @param {string} sheetName - Nombre de la hoja de cálculo
 * @param {string} filename - Nombre del archivo sin extensión
 * @param {Array<{wch: number}>} [colWidths=[]] - Anchos de columnas
 * @returns {void}
 */
export function downloadJSONAsXLSX(rows, sheetName, filename, colWidths = []) {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  if (colWidths.length > 0) ws['!cols'] = colWidths;
  const blob = new Blob(
    [XLSX.write(wb, { bookType: 'xlsx', type: 'array' })],
    { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
  );
  downloadFile(blob, `${filename}.xlsx`);
}

/**
 * Descarga datos JSON en formato CSV
 *
 * Convierte un array de objetos JSON a formato CSV y descarga el archivo.
 * Útil para exportar datos de tablas o listas.
 *
 * @param {Array<Object>} data - Array de objetos a exportar
 * @param {string} filename - Nombre del archivo (debe incluir extensión .csv)
 * @param {Array<string>} [headers] - Headers opcionales (si no se proveen, se usan las keys del primer objeto)
 *
 * @example
 * const users = [
 *   { nombre: 'Juan', email: 'juan@example.com' },
 *   { nombre: 'María', email: 'maria@example.com' }
 * ];
 * downloadJSONAsCSV(users, 'usuarios.csv');
 */
export function downloadJSONAsCSV(data, filename, headers = null) {
  if (!data || data.length === 0) {
    console.warn('No hay datos para exportar');
    return;
  }

  // Usar headers provistas o extraer del primer objeto
  const csvHeaders = headers || Object.keys(data[0]);

  // Crear línea de encabezados
  const headerLine = csvHeaders.join(',');

  // Crear líneas de datos
  const dataLines = data.map(row => {
    return csvHeaders.map(header => {
      const value = row[header] || '';
      // Escapar comillas y envolver en comillas si contiene comas
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    }).join(',');
  });

  // Combinar headers + datos
  const csvContent = [headerLine, ...dataLines].join('\n');

  // Descargar
  downloadCSV(csvContent, filename);
}
